import { supabase } from '../lib/supabase';

export const clientPortalService = {
  // Get client dashboard summary
  async getClientDashboardSummary(clientId) {
    try {
      const [consultations, assessments, recommendations, progressData] = await Promise.all([
        this.getClientConsultations(clientId),
        this.getClientAssessments(clientId),
        this.getClientRecommendations(clientId),
        this.getClientProgressTracking(clientId)
      ]);

      // Calculate current maturity score (average of latest scores)
      const latestScores = this.calculateLatestMaturityScores(progressData);
      const currentMaturityScore = latestScores?.averageScore;
      const maturityTrend = this.calculateMaturityTrend(progressData);

      return {
        currentMaturityScore,
        maturityTrend,
        totalConsultations: consultations?.length || 0,
        completedConsultations: consultations?.filter(c => c?.status === 'completed')?.length || 0,
        totalAssessments: assessments?.length || 0,
        activeRecommendations: recommendations?.filter(r => r?.status === 'active')?.length || 0,
        latestScores,
        consultations,
        assessments,
        recommendations,
        progressData
      };
    } catch (error) {
      console.error('Error fetching client dashboard summary:', error);
      throw error;
    }
  },

  // Get client consultations with session notes
  async getClientConsultations(clientId) {
    const { data, error } = await supabase
      ?.from('consultation_bookings')
      ?.select(`
        *,
        session_notes:consultation_session_notes(*)
      `)
      ?.eq('client_id', clientId)
      ?.order('scheduled_start', { ascending: false });

    if (error) throw error;

    return data?.map(booking => ({
      id: booking?.id,
      title: booking?.title,
      description: booking?.description,
      scheduledStart: booking?.scheduled_start,
      scheduledEnd: booking?.scheduled_end,
      durationMinutes: booking?.duration_minutes,
      status: booking?.status,
      serviceInterest: booking?.service_interest,
      meetingNotes: booking?.meeting_notes,
      sessionNotes: booking?.session_notes?.[0] ? {
        sessionSummary: booking?.session_notes?.[0]?.session_summary,
        actionItems: booking?.session_notes?.[0]?.action_items,
        followUpRecommendations: booking?.session_notes?.[0]?.follow_up_recommendations,
        keyInsights: booking?.session_notes?.[0]?.key_insights,
        nextSteps: booking?.session_notes?.[0]?.next_steps,
        attachments: booking?.session_notes?.[0]?.attachments
      } : null,
      createdAt: booking?.created_at
    })) || [];
  },

  // Get client assessments
  async getClientAssessments(clientId) {
    const { data, error } = await supabase
      ?.from('assessment_results')
      ?.select('*')
      ?.eq('user_id', clientId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(assessment => ({
      id: assessment?.id,
      assessmentType: assessment?.assessment_type,
      title: assessment?.title,
      formData: assessment?.form_data,
      results: assessment?.results,
      createdAt: assessment?.created_at,
      updatedAt: assessment?.updated_at
    })) || [];
  },

  // Get client recommendations
  async getClientRecommendations(clientId, status = null) {
    let query = supabase
      ?.from('client_recommendations')
      ?.select('*')
      ?.eq('client_id', clientId);

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query?.order('priority', { ascending: true });

    if (error) throw error;

    return data?.map(rec => ({
      id: rec?.id,
      assessmentId: rec?.assessment_id,
      title: rec?.title,
      description: rec?.description,
      category: rec?.category,
      priority: rec?.priority,
      status: rec?.status,
      recommendedActions: rec?.recommended_actions,
      estimatedImpact: rec?.estimated_impact,
      resources: rec?.resources,
      createdAt: rec?.created_at,
      updatedAt: rec?.updated_at,
      completedAt: rec?.completed_at
    })) || [];
  },

  // Update recommendation status
  async updateRecommendationStatus(recommendationId, status) {
    const updates = {
      status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'completed') {
      updates.completed_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase
      ?.from('client_recommendations')
      ?.update(updates)
      ?.eq('id', recommendationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Get client progress tracking
  async getClientProgressTracking(clientId) {
    const { data, error } = await supabase
      ?.from('client_progress_tracking')
      ?.select('*')
      ?.eq('client_id', clientId)
      ?.order('tracked_at', { ascending: false });

    if (error) throw error;

    return data?.map(progress => ({
      id: progress?.id,
      assessmentId: progress?.assessment_id,
      dimension: progress?.dimension,
      score: progress?.score,
      previousScore: progress?.previous_score,
      improvementPercentage: progress?.improvement_percentage,
      milestoneAchieved: progress?.milestone_achieved,
      notes: progress?.notes,
      trackedAt: progress?.tracked_at,
      createdAt: progress?.created_at
    })) || [];
  },

  // Get progress by dimension
  async getProgressByDimension(clientId, dimension) {
    const { data, error } = await supabase
      ?.from('client_progress_tracking')
      ?.select('*')
      ?.eq('client_id', clientId)
      ?.eq('dimension', dimension)
      ?.order('tracked_at', { ascending: true });

    if (error) throw error;

    return data?.map(progress => ({
      score: progress?.score,
      trackedAt: progress?.tracked_at,
      milestoneAchieved: progress?.milestone_achieved
    })) || [];
  },

  // Calculate latest maturity scores
  calculateLatestMaturityScores(progressData) {
    const dimensions = [
      'data_infrastructure',
      'analytics_capabilities',
      'governance_maturity',
      'team_skills',
      'strategic_alignment'
    ];

    const latestScores = {};
    let totalScore = 0;
    let dimensionCount = 0;

    dimensions?.forEach(dimension => {
      const dimensionData = progressData?.filter(p => p?.dimension === dimension);
      if (dimensionData?.length > 0) {
        const latest = dimensionData?.[0]; // Already sorted by tracked_at desc
        latestScores[dimension] = {
          score: latest?.score,
          previousScore: latest?.previousScore,
          improvementPercentage: latest?.improvementPercentage,
          milestoneAchieved: latest?.milestoneAchieved
        };
        totalScore += latest?.score;
        dimensionCount++;
      }
    });

    const averageScore = dimensionCount > 0 ? Math.round(totalScore / dimensionCount) : 0;

    return {
      ...latestScores,
      averageScore
    };
  },

  // Calculate maturity trend
  calculateMaturityTrend(progressData) {
    if (!progressData || progressData?.length === 0) {
      return { direction: 'stable', percentage: 0 };
    }

    // Group by dimension and get latest two scores
    const dimensions = [
      'data_infrastructure',
      'analytics_capabilities',
      'governance_maturity',
      'team_skills',
      'strategic_alignment'
    ];

    let totalImprovement = 0;
    let dimensionCount = 0;

    dimensions?.forEach(dimension => {
      const dimensionData = progressData?.filter(p => p?.dimension === dimension);
      if (dimensionData?.length >= 2) {
        const latest = dimensionData?.[0];
        const previous = dimensionData?.[1];
        const improvement = ((latest?.score - previous?.score) / previous?.score) * 100;
        totalImprovement += improvement;
        dimensionCount++;
      }
    });

    if (dimensionCount === 0) {
      return { direction: 'stable', percentage: 0 };
    }

    const averageImprovement = totalImprovement / dimensionCount;

    return {
      direction: averageImprovement > 0 ? 'up' : averageImprovement < 0 ? 'down' : 'stable',
      percentage: Math.abs(Math.round(averageImprovement))
    };
  },

  // Get assessment comparison data
  async getAssessmentComparison(clientId, assessmentIds) {
    const { data, error } = await supabase
      ?.from('assessment_results')
      ?.select('*')
      ?.eq('user_id', clientId)
      ?.in('id', assessmentIds)
      ?.order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map(assessment => ({
      id: assessment?.id,
      title: assessment?.title,
      assessmentType: assessment?.assessment_type,
      results: assessment?.results,
      createdAt: assessment?.created_at
    })) || [];
  },

  // Get time-series progress data for charts
  getTimeSeriesProgressData(progressData) {
    const dimensions = [
      'data_infrastructure',
      'analytics_capabilities',
      'governance_maturity',
      'team_skills',
      'strategic_alignment'
    ];

    // Group by tracked date
    const dateMap = new Map();

    progressData?.forEach(progress => {
      const date = new Date(progress?.trackedAt)?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });

      if (!dateMap?.has(date)) {
        dateMap?.set(date, {
          date,
          data_infrastructure: 0,
          analytics_capabilities: 0,
          governance_maturity: 0,
          team_skills: 0,
          strategic_alignment: 0
        });
      }

      const entry = dateMap?.get(date);
      entry[progress?.dimension] = progress?.score;
    });

    return Array.from(dateMap?.values());
  }
};